import * as THREE from 'three';

import {
  type LabPalette,
  type LabSceneFactory,
  createLabStage,
  labGlassEdgeMaterial,
  labGlassMaterial,
  refractiveLiquidMaterial,
} from './labScene3d';
import type { IndicatorId } from './titration';

/**
 * Titration bench shared by pH & Titration and Volumetric Analysis: a burette in a clamp on a
 * retort stand above a conical flask on a white tile. Every value comes from the tool's model.
 */
export interface TitrationSceneState {
  /** Titrant delivered from the burette, 0–50 mL. The burette level falls as this grows. */
  titrantAddedMl: number;
  /** Liquid in the flask (aliquot + titrant), mL. The flask level rises with it. */
  flaskVolumeMl: number;
  indicator: IndicatorId;
  /** 0 = the indicator's acid colour, 1 = its base colour (from the model pH). */
  shade: number;
  /** A drop falls from the burette tip while titrant is being delivered. */
  dropping: boolean;
}

// World units. A teaching view: the burette and flask are drawn in proportion to each other.
const BURETTE_CAPACITY_ML = 50;
/** The flask is drawn as a 125 mL conical flask so a 20–25 mL aliquot is easy to see. */
const FLASK_CAPACITY_ML = 125;
const BENCH_Y = -1.82;
const PLATE_H = 0.12;
const TILE_H = 0.04;
const FLASK_FLOOR = BENCH_Y + PLATE_H + TILE_H;
const FLASK = { baseR: 0.85, coneH: 1.45, neckR: 0.29, neckH: 0.5, wall: 0.045 } as const;
const BURETTE = { r: 0.12, inner: 0.095, bottom: 0.88, top: 5.55, zeroY: 5.1, fiftyY: 1.25 } as const;
const STOPCOCK_Y = 0.74;
const TIP = { top: 0.6, bottom: 0.12 } as const;
const ROD = { x: -1.75, z: -0.15, r: 0.06, top: 6.1 } as const;
const CLAMP_Y = 3.75;

/** Indicator colours from palette tokens: [acid colour, middle, base colour]. */
function indicatorColours(palette: LabPalette, indicator: IndicatorId): [THREE.Color, THREE.Color, THREE.Color] {
  const white = palette.glass.clone();
  if (indicator === 'phenolphthalein') {
    const colourless = palette.liquid.clone().lerp(white, 0.9);
    return [colourless, palette.accent.clone().lerp(white, 0.55), palette.accent.clone().lerp(white, 0.15)];
  }
  if (indicator === 'methyl-orange') {
    return [palette.heat.clone(), palette.heat.clone().lerp(palette.glow, 0.5), palette.glow.clone()];
  }
  return [palette.glow.clone(), palette.glow.clone().lerp(palette.liquid, 0.5), palette.liquid.clone()];
}

export function indicatorSceneColour(palette: LabPalette, indicator: IndicatorId, shade: number): THREE.Color {
  const [low, middle, high] = indicatorColours(palette, indicator);
  const s = THREE.MathUtils.clamp(shade, 0, 1);
  return s <= 0.5 ? low.lerp(middle, s * 2) : middle.lerp(high, (s - 0.5) * 2);
}

/** Inner radius of the flask cone at height y above the inner floor. */
const coneRadius = (y: number) => FLASK.baseR - FLASK.wall - ((FLASK.baseR - FLASK.neckR) * y) / FLASK.coneH;
const frustumVolume = (h: number) => {
  const r0 = coneRadius(0);
  const r1 = coneRadius(h);
  return (Math.PI / 3) * h * (r0 * r0 + r0 * r1 + r1 * r1);
};
const FULL_CONE_VOLUME = frustumVolume(FLASK.coneH - 0.02);

/** Liquid height in the cone for a volume in mL (bisection on the frustum volume). */
export function flaskLiquidHeight(volumeMl: number): number {
  const target = (THREE.MathUtils.clamp(volumeMl, 0, FLASK_CAPACITY_ML) / FLASK_CAPACITY_ML) * FULL_CONE_VOLUME;
  let low = 0;
  let high = FLASK.coneH - 0.02;
  for (let iteration = 0; iteration < 40; iteration += 1) {
    const middle = (low + high) / 2;
    if (frustumVolume(middle) < target) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
}

/** Burette liquid top for a delivered volume: 0 mL at the top mark, 50 mL at the bottom mark. */
export function buretteLevelY(addedMl: number): number {
  const share = THREE.MathUtils.clamp(addedMl, 0, BURETTE_CAPACITY_ML) / BURETTE_CAPACITY_ML;
  return BURETTE.zeroY - share * (BURETTE.zeroY - BURETTE.fiftyY);
}

function flaskProfile(): THREE.Vector2[] {
  const { baseR, coneH, neckR, neckH, wall } = FLASK;
  const top = coneH + neckH;
  return [
    [0, 0], [baseR - 0.12, 0], [baseR - 0.02, 0.03], [baseR, 0.1],
    [neckR + 0.04, coneH - 0.04], [neckR, coneH + 0.06], [neckR, top - 0.04], [neckR + 0.05, top], [neckR + 0.04, top + 0.04],
    [neckR - wall, top + 0.02], [neckR - wall, coneH + 0.06], [neckR + 0.04 - wall, coneH - 0.04 - wall],
    [baseR - wall, 0.1], [baseR - wall - 0.06, wall], [0, wall],
  ].map(([x, y]) => new THREE.Vector2(x, y));
}

export const createTitrationScene: LabSceneFactory<TitrationSceneState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion, {
    cameraPosition: [0.9, 2.8, 15.6],
    target: [-0.2, 2.0, 0],
    benchY: BENCH_Y,
  });
  const { scene, keep } = stage;
  const metal = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal, metalness: 0.75, roughness: 0.3, clearcoat: 0.4 }));

  // Retort stand: base plate, rod, boss and clamp arm with jaws around the burette.
  const plate = new THREE.Mesh(keep(new THREE.BoxGeometry(3.5, PLATE_H, 2.3)), metal);
  plate.position.set(-0.55, BENCH_Y + PLATE_H / 2, -0.1);
  const rod = new THREE.Mesh(keep(new THREE.CylinderGeometry(ROD.r, ROD.r, ROD.top - BENCH_Y, 24)), metal);
  rod.position.set(ROD.x, (ROD.top + BENCH_Y) / 2, ROD.z);
  const boss = new THREE.Mesh(keep(new THREE.BoxGeometry(0.26, 0.26, 0.26)), metal);
  boss.position.set(ROD.x, CLAMP_Y, ROD.z);
  const armLength = Math.hypot(ROD.x, ROD.z) - BURETTE.r - 0.05;
  const arm = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.04, 0.04, armLength, 16)), metal);
  arm.rotation.z = Math.PI / 2;
  arm.rotation.y = Math.atan2(ROD.z, -ROD.x);
  arm.position.set(ROD.x / 2 - 0.02, CLAMP_Y, ROD.z / 2);
  const jawMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.bench.clone().lerp(palette.metal, 0.15), roughness: 0.7 }));
  const jaws = new THREE.Mesh(keep(new THREE.TorusGeometry(BURETTE.r + 0.04, 0.045, 12, 40)), jawMaterial);
  jaws.rotation.x = Math.PI / 2;
  jaws.position.y = CLAMP_Y;
  [plate, rod, boss, arm, jaws].forEach((mesh) => {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
  });
  scene.add(plate, rod, boss, arm, jaws);

  // White tile under the flask, so a colour change is easy to see.
  const tile = new THREE.Mesh(
    keep(new THREE.BoxGeometry(2.1, TILE_H, 2.1)),
    keep(new THREE.MeshPhysicalMaterial({ color: palette.bench, roughness: 0.15, clearcoat: 1 })),
  );
  tile.position.set(0, BENCH_Y + PLATE_H + TILE_H / 2, 0);
  tile.receiveShadow = true;
  scene.add(tile);

  // Conical flask: lathe glass with a thicker rim and base edge.
  const flask = new THREE.Mesh(keep(new THREE.LatheGeometry(flaskProfile(), 96)), labGlassMaterial(stage));
  flask.position.y = FLASK_FLOOR;
  flask.renderOrder = 10;
  const edge = labGlassEdgeMaterial(stage);
  const flaskRim = new THREE.Mesh(keep(new THREE.TorusGeometry(FLASK.neckR + 0.03, 0.035, 10, 64)), edge);
  flaskRim.rotation.x = Math.PI / 2;
  flaskRim.position.y = FLASK_FLOOR + FLASK.coneH + FLASK.neckH + 0.02;
  const flaskBase = new THREE.Mesh(keep(new THREE.TorusGeometry(FLASK.baseR - 0.05, 0.04, 10, 96)), edge);
  flaskBase.rotation.x = Math.PI / 2;
  flaskBase.position.y = FLASK_FLOOR + 0.06;
  flaskRim.renderOrder = 11;
  flaskBase.renderOrder = 11;
  scene.add(flask, flaskRim, flaskBase);

  // Flask liquid: a refractive frustum rebuilt only when the level changes.
  const flaskLiquidMaterial = refractiveLiquidMaterial(stage, palette.liquid, { attenuationDistance: 1.6 });
  const flaskLiquid = new THREE.Mesh(new THREE.BufferGeometry(), flaskLiquidMaterial);
  scene.add(flaskLiquid);
  let liquidHeight = -1;
  const setFlaskLevel = (height: number) => {
    if (Math.abs(height - liquidHeight) < 0.0005) return;
    liquidHeight = height;
    flaskLiquid.geometry.dispose();
    flaskLiquid.visible = height > 0.005;
    flaskLiquid.geometry = new THREE.CylinderGeometry(coneRadius(height) - 0.004, coneRadius(0) - 0.004, Math.max(height, 0.001), 72);
    flaskLiquid.position.y = FLASK_FLOOR + FLASK.wall + height / 2;
  };

  // Burette: open glass tube, graduations, stopcock and tapered tip.
  const tubeMaterial = labGlassMaterial(stage, 0.3);
  tubeMaterial.side = THREE.DoubleSide;
  const tubeLength = BURETTE.top - BURETTE.bottom;
  const tube = new THREE.Mesh(keep(new THREE.CylinderGeometry(BURETTE.r, BURETTE.r, tubeLength, 32, 1, true)), tubeMaterial);
  tube.position.y = BURETTE.bottom + tubeLength / 2;
  tube.renderOrder = 12;
  const buretteRim = new THREE.Mesh(keep(new THREE.TorusGeometry(BURETTE.r, 0.022, 8, 32)), edge);
  buretteRim.rotation.x = Math.PI / 2;
  buretteRim.position.y = BURETTE.top;
  scene.add(tube, buretteRim);

  const markMaterial = keep(new THREE.MeshBasicMaterial({ color: palette.metal, transparent: true, opacity: 0.75 }));
  const markGeometry = keep(new THREE.BoxGeometry(1, 0.012, 0.01));
  for (let ml = 0; ml <= BURETTE_CAPACITY_ML; ml += 1) {
    const tick = new THREE.Mesh(markGeometry, markMaterial);
    tick.scale.x = ml % 10 === 0 ? 0.11 : ml % 5 === 0 ? 0.08 : 0.045;
    tick.position.set(0, buretteLevelY(ml), BURETTE.r + 0.004);
    tick.renderOrder = 13;
    scene.add(tick);
  }

  const titrantMaterial = refractiveLiquidMaterial(stage, palette.liquid, { tint: 0.75, attenuationDistance: 6 });
  const titrant = new THREE.Mesh(keep(new THREE.CylinderGeometry(BURETTE.inner, BURETTE.inner, 1, 32)), titrantMaterial);
  scene.add(titrant);

  const stopcockMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.liquid.clone().lerp(palette.metal, 0.35), roughness: 0.35, clearcoat: 0.6 }));
  const barrel = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.1, 0.1, 0.34, 24)), labGlassEdgeMaterial(stage));
  barrel.rotation.z = Math.PI / 2;
  barrel.position.y = STOPCOCK_Y;
  const key = new THREE.Mesh(keep(new THREE.BoxGeometry(0.08, 0.36, 0.06)), stopcockMaterial);
  key.position.set(0.26, STOPCOCK_Y, 0);
  const stem = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.06, 0.06, BURETTE.bottom - TIP.top + 0.02, 16)), labGlassEdgeMaterial(stage));
  stem.position.y = (BURETTE.bottom + TIP.top) / 2;
  const tip = new THREE.Mesh(keep(new THREE.CylinderGeometry(0.06, 0.022, TIP.top - TIP.bottom, 16)), labGlassEdgeMaterial(stage));
  tip.position.y = (TIP.top + TIP.bottom) / 2;
  key.castShadow = true;
  scene.add(barrel, key, stem, tip);

  // One falling drop (transparent, not refractive: the flask liquid cannot see refractive objects).
  const dropMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.liquid.clone().lerp(palette.glass, 0.6), transparent: true, opacity: 0.8, roughness: 0.05, clearcoat: 1 }));
  const drop = new THREE.Mesh(keep(new THREE.SphereGeometry(0.035, 16, 12)), dropMaterial);
  drop.scale.y = 1.3;
  scene.add(drop);

  stage.finish();
  const colour = new THREE.Color();
  const white = palette.glass.clone();

  return {
    update: (state, t, playing) => {
      // Burette: liquid from the stopcock up to the current reading.
      const level = buretteLevelY(state.titrantAddedMl);
      const columnBottom = BURETTE.bottom + 0.02;
      titrant.scale.y = Math.max(level - columnBottom, 0.001);
      titrant.position.y = columnBottom + (level - columnBottom) / 2;

      // Flask: the level follows the volume, the colour follows the indicator and pH.
      const height = flaskLiquidHeight(state.flaskVolumeMl);
      setFlaskLevel(height);
      colour.copy(indicatorSceneColour(palette, state.indicator, state.shade));
      flaskLiquidMaterial.color.copy(colour).lerp(white, 0.25);
      flaskLiquidMaterial.attenuationColor.copy(colour);

      // Drop: falls from the tip to the liquid surface while titrant is being delivered.
      const surfaceY = FLASK_FLOOR + FLASK.wall + height;
      drop.visible = state.dropping && playing && !reducedMotion;
      if (drop.visible) {
        const progress = (t * 1.6) % 1;
        drop.position.set(0, TIP.bottom - 0.04 - progress * progress * (TIP.bottom - 0.04 - surfaceY), 0);
      }
      stage.render(playing);
    },
    resize: stage.resize,
    dispose: () => {
      flaskLiquid.geometry.dispose();
      stage.dispose();
    },
  };
};

/** Named states for screenshot review of every visible change. */
export const TITRATION_PREVIEW_STATES: Record<string, TitrationSceneState> = {
  start: { titrantAddedMl: 0, flaskVolumeMl: 25, indicator: 'phenolphthalein', shade: 0, dropping: false },
  nearEndpoint: { titrantAddedMl: 24.5, flaskVolumeMl: 49.5, indicator: 'phenolphthalein', shade: 0, dropping: true },
  endpoint: { titrantAddedMl: 25.05, flaskVolumeMl: 50.05, indicator: 'phenolphthalein', shade: 0.35, dropping: false },
  pastEndpoint: { titrantAddedMl: 30, flaskVolumeMl: 55, indicator: 'phenolphthalein', shade: 1, dropping: false },
  buretteLimit: { titrantAddedMl: 50, flaskVolumeMl: 75, indicator: 'phenolphthalein', shade: 1, dropping: false },
  methylOrangeEndpoint: { titrantAddedMl: 18.15, flaskVolumeMl: 38.15, indicator: 'methyl-orange', shade: 0.5, dropping: false },
};
